import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import InputField from "./InputField";
import SpeakerField from "./SpeakerField";
import FileUpload from "./FileUpload";
import TagOptions from "./TagOptions";
import SearchableSelect from "../../ui/SearchableSelect";
import { transcriptApi } from "../../../api/transcriptApi";
import { wpApi } from "../../../api/wpApi";
import { faiconApi } from "../../../api/faiconApi";
import { extractUniqueDatesWithEventIDs, extractWordPressChildEventDates, extractFaiconSessionDates } from "../../../services/DateExtractor";
import { extractUniqueDays, htmlToText } from "../../../services/FaiconHelpers";
import { useFileUpload } from "../../../hooks/useFileUpload";
import { fileApi } from "../../../api/fileApi";
import Modal from "../../ui/Modal";
import Notification from "../../ui/Notification";
import { 
  CloudArrowUpIcon, 
  DocumentTextIcon, 
  UserGroupIcon,
  TagIcon,
  CalendarDaysIcon,
  AcademicCapIcon,
  XMarkIcon,
  CheckCircleIcon,
  TrashIcon
} from '@heroicons/react/24/outline';
import { TRANSCRIPT_PROVIDERS, DEFAULT_PROVIDER } from '../../../constants/transcriptProviders';

const FAICON_API_KEY = process.env.REACT_APP_FAICON_APP_TOKEN;
const FAICON_API_URL = process.env.REACT_APP_FAICON_APP_URL;
const FAICON_API_EXISTS = FAICON_API_KEY && FAICON_API_URL;

export default function TranscriptUploader() {
  const navigate = useNavigate();
  const [file, setFile] = useState(null);
  const [uploadedFileId, setUploadedFileId] = useState(null);
  const [isFileUploaded, setIsFileUploaded] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [notification, setNotification] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [selectedSource, setSelectedSource] = useState(null);
  const [isLoadingEvents, setIsLoadingEvents] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [createdSessionId, setCreatedSessionId] = useState(null); // Track created session
  const formDataInitialState = useMemo(
    () => ({
      user_id: "",
      file_id: "",
      file_type: "",
      event_name: "",
      event_day: "",
      event_id: "",
      title: "",
      tags: [],
      wp_id: "",
      upload_date: "",
      description: "",
      moderator: "",
      speakers: [],
      faicon_session_id: "",
      transcript_status: "",
      transcript: {},
      report_status: "",
      report: {},
      report_url: "",
      auto_generate_transcript: true,
      transcript_provider: DEFAULT_PROVIDER,
    }),
    []
  );
  const [formData, setFormData] = useState(formDataInitialState);
  const user_id = "123";
  const [events, setEvents] = useState([]);
  const [days, setDays] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [selectedDay, setSelectedDay] = useState(null);
  const [typeOfTitle, setTypeOfTitle] = useState("WordPress");
  const [speakers, setSpeakers] = useState([""]);
  const [selectedProvider, setSelectedProvider] = useState(DEFAULT_PROVIDER);

  // Use the new file upload hook
  const { uploadFile, uploading, progress, uploadedFile, error: uploadError, resetUpload } = useFileUpload();

  // Reset state function
  const resetState = useCallback(() => {
    setDays([]);
    setSessions([]);
    setSelectedEvent(null);
    setSelectedDay(null);
    setSpeakers([""]);
    setFormData(formDataInitialState);
    setSelectedSource(null);
    setShowForm(false);
    setIsLoadingEvents(false);
    setSelectedProvider(DEFAULT_PROVIDER);
    setCreatedSessionId(null); // Clear created session ID
  }, [formDataInitialState]);

  // Set user_id, uploaded date and initial status in formData
  useEffect(() => {
    setFormData((prevData) => ({
      ...prevData,
      user_id: user_id,
    }));
  }, [user_id]);

  // Handle source selection
  const handleSourceSelection = async (source) => {
    
    // Reset form and hide it when changing source
    if (selectedSource && selectedSource !== source) {
      resetState();
      setShowForm(false);
    }
    
    setSelectedSource(source);
    setTypeOfTitle(source);
    
    // If Custom is selected, show form immediately
    if (source === "Custom") {
      setShowForm(true);
      setIsLoadingEvents(false);
      return;
    }
    
    // For WordPress and Faicon, show loading and fetch events
    const startTime = Date.now();
    setIsLoadingEvents(true);
    setShowForm(false); // Hide form while loading
    
    try {
      let eventsData;
      if (source === "WordPress") {
        const wpEvents = await wpApi.getEventsWithChildren();
        eventsData = wpEvents.map(event => ({
          id: event.ID,
          title: event.post_title,
          slug: event.post_name,
          children: event.children || []
        }));
      } else if (source === "Faicon") {
        const faiconResponse = await faiconApi.getAllEvents();
        console.log('Faicon API response:', faiconResponse);
        
        // Check if response has data or results property, or if it's directly an array
        let faiconEvents;
        if (faiconResponse && Array.isArray(faiconResponse)) {
          faiconEvents = faiconResponse;
        } else if (faiconResponse && Array.isArray(faiconResponse.data)) {
          faiconEvents = faiconResponse.data;
        } else if (faiconResponse && Array.isArray(faiconResponse.results)) {
          faiconEvents = faiconResponse.results;
        } else {
          console.error('Faicon API response is not an array:', faiconResponse);
          throw new Error('Invalid response format from Faicon API');
        }
        
        eventsData = faiconEvents.map(event => ({
          id: event.id,
          title: event.name || event.title,
          sessions: event.sessions || []
        }));
      }
      
      const loadingTime = Date.now() - startTime;
      
      setEvents(eventsData || []);
      
      // Show form immediately after loading completes
      setShowForm(true);
      setIsLoadingEvents(false);
      
      
    } catch (error) {
      const loadingTime = Date.now() - startTime;
      setNotification({
        type: 'error',
        title: 'Loading Failed',
        message: `Failed to load events from ${source} after ${loadingTime}ms. Please try again.`
      });
      setIsLoadingEvents(false);
      setSelectedSource(null);
      setShowForm(false);
    }
  };

  // Get days from selected event
  useEffect(() => {
    if (selectedEvent && typeOfTitle !== "Custom") {
      const fetchDays = async () => {
        try {
          let daysData;
          if (typeOfTitle === "WordPress") {
            // For WordPress, we need to fetch child events and group by day
            const childEvents = await wpApi.getChildEvents(selectedEvent.id);
            
            // Use the new WordPress-specific function
            daysData = extractWordPressChildEventDates(childEvents);
          } else {
            // For Faicon, fetch sessions for the event and then extract days
            const faiconResponse = await faiconApi.getSessionsByEvent(selectedEvent.id);
            
            // Extract sessions from the response (Faicon returns { count, next, previous, results: [...] })
            const faiconSessions = faiconResponse?.results || faiconResponse || [];
            
            // Use the new Faicon-specific function
            daysData = extractFaiconSessionDates(faiconSessions);
          }
          setDays(daysData || []);
        } catch (error) {
          console.error('Error extracting days:', error);
          setDays([]);
        }
      };
      fetchDays();
    } else {
      setDays([]);
    }
  }, [selectedEvent, typeOfTitle]);

  // Get sessions from selected day
  useEffect(() => {
    if (selectedDay && typeOfTitle !== "Custom") {
      const fetchSessions = async () => {
        try {
          let sessionsData;
          if (typeOfTitle === "WordPress") {
            // For WordPress, use sessions already stored in selectedDay
            sessionsData = selectedDay.sessions || [];
          } else {
            // For Faicon, use sessions already stored in selectedDay
            sessionsData = selectedDay.sessions || [];
          }
          setSessions(sessionsData || []);
        } catch (error) {
          console.error('Error fetching sessions:', error);
          setSessions([]);
        }
      };
      fetchSessions();
    } else {
      setSessions([]);
    }
  }, [selectedDay, typeOfTitle]);

  // Handle file upload
  const handleFileUpload = async (uploadedFile) => {
    setFile(uploadedFile);
    setFormData((prevData) => ({
      ...prevData,
      file_type: uploadedFile.type,
    }));
    
    // Automatically start upload when file is selected
    try {
      const uploadResult = await uploadFile(uploadedFile);
      console.log('Upload result:', uploadResult);
      
      if (uploadResult && uploadResult.id) {
        console.log('Setting uploadedFileId to:', uploadResult.id);
        setUploadedFileId(uploadResult.id);
        setIsFileUploaded(true);
        
        // Update form data with file information
        setFormData((prevData) => ({
          ...prevData,
          file_id: uploadResult.id,
          file_type: uploadResult.mime_type,
        }));
        
        setNotification({
          type: 'success',
          title: 'Upload Successful!',
          message: 'File uploaded successfully! You can now fill out the form and submit.'
        });
      } else {
        console.log('No file_id in upload result:', uploadResult);
      }
    } catch (error) {
      console.error('File upload failed:', error);
      setNotification({
        type: 'error',
        title: 'Upload Failed',
        message: 'File upload failed. Please try again.'
      });
      setFile(null);
      setUploadedFileId(null);
      setIsFileUploaded(false);
    }
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!isFileUploaded || !uploadedFileId) {
      setNotification({
        type: 'error',
        title: 'Form Submission Error',
        message: 'Please upload a file first before submitting the form.'
      });
      return;
    }

    try {
      // Prepare session data according to API specification
      const sessionData = {
        upload_id: uploadedFileId,
        title: formData.title,
        description: formData.description,
        auto_generate_transcript: formData.auto_generate_transcript,
      };

      // Include transcript provider if auto-generate is enabled
      if (formData.auto_generate_transcript) {
        sessionData.transcript_provider = selectedProvider;
      }

      // Include exactly one of: wp_id, faicon_id, or event_data
      if (selectedSource === "WordPress" && formData.wp_id) {
        sessionData.wp_id = String(formData.wp_id);
        // Include WordPress session/page ID if selected
        if (formData.faicon_session_id) {
          sessionData.wp_session_id = String(formData.faicon_session_id);
        }
        // Also send event_data with the actual event name for proper event naming
        sessionData.event_data = { title: selectedEvent?.title || formData.title };
        if (formData.description && formData.description.trim() !== "") {
          sessionData.event_data.description = formData.description;
        }
      } else if (selectedSource === "Faicon" && formData.faicon_id) {
        sessionData.faicon_id = String(formData.faicon_id);
        // Include FAICON session ID if selected
        if (formData.faicon_session_id) {
          sessionData.faicon_session_id = String(formData.faicon_session_id);
        }
        // Also send event_data with the actual event name for proper event naming
        sessionData.event_data = { title: selectedEvent?.title || formData.title };
        if (formData.description && formData.description.trim() !== "") {
          sessionData.event_data.description = formData.description;
        }
      } else {
        sessionData.event_data = { title: selectedEvent?.title || formData.title };
        if (formData.description && formData.description.trim() !== "") {
          sessionData.event_data.description = formData.description;
        }
      }

      console.log('Submitting session data:', sessionData);

      // Create session record
      const sessionResult = await transcriptApi.createSession(sessionData);
      
      if (sessionResult) {
        // Store created session ID for queue monitoring
        setCreatedSessionId(sessionResult.id);
        
        // Show notification with queue information if available
        let message = 'Session created successfully!';
        if (sessionResult.queue_position !== null && sessionResult.queue_position !== undefined) {
          message += ` Added to queue at position #${sessionResult.queue_position}.`;
          if (sessionResult.estimated_wait_time) {
            const mins = Math.floor(sessionResult.estimated_wait_time / 60);
            const secs = sessionResult.estimated_wait_time % 60;
            if (mins === 0) {
              message += ` Estimated wait time: ${secs}sec`;
            } else if (secs === 0) {
              message += ` Estimated wait time: ${mins}min`;
            } else {
              message += ` Estimated wait time: ${mins}min i ${secs}sec`;
            }
          }
        }
        
        setNotification({
          type: 'success',
          title: 'Session Created!',
          message: message
        });

        // Redirect to sessions list after successful creation
        navigate('/sessions');
        
        // Reset form and upload state (but keep createdSessionId for monitoring)
        setFile(null);
        setUploadedFileId(null);
        setIsFileUploaded(false);
        setFormData(formDataInitialState);
        resetUpload();
        // Don't reset state completely - we want to show queue status
        setShowForm(false);
      } else {
        setNotification({
          type: 'warning',
          title: 'Session Creation Warning',
          message: 'Session creation failed'
        });
      }
    } catch (error) {
      console.error('Session creation error:', error);
      setNotification({
        type: 'error',
        title: 'Session Creation Error',
        message: error.message || 'Session creation failed. Please try again.'
      });
    }
  };

  // Handle input changes
  const handleInputChange = (name, value) => {
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  // Handle speaker changes
  const handleSpeakerChange = (newSpeakers) => {
    setSpeakers(newSpeakers);
    setFormData((prevData) => ({
      ...prevData,
      speakers: newSpeakers.filter((speaker) => speaker.trim() !== ""),
    }));
  };

  // Handle tag changes
  const handleTagChange = (tags) => {
    setFormData((prevData) => ({
      ...prevData,
      tags,
    }));
  };

  // Handle file deletion
  const handleDeleteFile = async () => {
    console.log('handleDeleteFile called with uploadedFileId:', uploadedFileId);
    
    if (!uploadedFileId) {
      console.log('No uploadedFileId, returning early');
      return;
    }
    
    setIsDeleting(true);
    try {
      console.log('Calling fileApi.deleteFile with ID:', uploadedFileId);
      
      // Call the backend delete API
      const result = await fileApi.deleteFile(uploadedFileId);
      console.log('Delete API response:', result);
      
      setNotification({
        type: 'success',
        title: 'File Deleted',
        message: 'File has been deleted successfully. You can now upload a new file.'
      });
      
      // Reset all states
      setFile(null);
      setUploadedFileId(null);
      setIsFileUploaded(false);
      setFormData(formDataInitialState);
      resetUpload();
      // Don't call resetState here as it resets showDeleteModal
      // resetState();
      setShowDeleteModal(false);
    } catch (error) {
      console.error('File deletion error:', error);
      setNotification({
        type: 'error',
        title: 'Deletion Failed',
        message: `Failed to delete file: ${error.message || 'Unknown error'}. Please try again.`
      });
    } finally {
      setIsDeleting(false);
    }
  };

  // Check if form can be submitted
  const canSubmit = formData.title && formData.title.trim() !== "" && file;



  const sourceOptions = [
    {
      key: "WordPress",
      title: "WordPress",
      description: "Import from WordPress events",
    },
    {
      key: "Faicon",
      title: "FAICON",
      description: "Import from FAICON events",
    },
    {
      key: "Custom",
      title: "Custom",
      description: "Enter event details manually",
    },
  ];

  return (
    <div className="upload-uploader">
      <div className="upload-card">
        <div className="upload-card-head">
          <AcademicCapIcon />
          <h2>Content source</h2>
          <p>Pick where your content comes from</p>
        </div>
        <div className="upload-card-body">
          <div className="upload-source-grid">
            {sourceOptions.map(option => {
              const isActive = selectedSource === option.key;
              return (
                <button
                  key={option.key}
                  type="button"
                  className={`upload-source-card ${isActive ? 'active' : ''}`}
                  onClick={() => handleSourceSelection(option.key)}
                  aria-pressed={isActive}
                >
                  <span className="upload-source-icon">
                    <AcademicCapIcon />
                  </span>
                  <span className="upload-source-text">
                    <span className="upload-source-title">{option.title}</span>
                    <span className="upload-source-desc">{option.description}</span>
                  </span>
                  {isActive && <span className="upload-source-dot" aria-hidden="true" />}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Form - Show when source is selected */}
      {showForm && (
        <form onSubmit={handleSubmit} className="upload-form">
          {/* Event Selection for WordPress and Faicon */}
          {selectedSource && selectedSource !== "Custom" && (
            <div className="upload-card">
              <div className="upload-card-head">
                <CalendarDaysIcon />
                <h2>Event selection</h2>
                <p>Choose the event, day and session this transcript belongs to</p>
              </div>
              <div className="upload-card-body">
                {/* Loading State */}
                {isLoadingEvents && (
                  <div className="upload-loading">
                    <div className="upload-spinner" />
                    <p>Loading events from {selectedSource}...</p>
                  </div>
                )}

                {/* Event Selection */}
                {!isLoadingEvents && events.length > 0 && (
                  <div className="upload-field">
                    <label className="upload-field-label">Event *</label>
                    <SearchableSelect
                      options={events}
                      value={selectedEvent}
                      onChange={(event) => {
                        setSelectedEvent(event);
                        if (selectedSource === "WordPress") {
                          setFormData(prev => ({
                            ...prev,
                            wp_id: event?.id || "",
                            faicon_id: ""
                          }));
                        } else if (selectedSource === "Faicon") {
                          setFormData(prev => ({
                            ...prev,
                            faicon_id: event?.id || "",
                            wp_id: ""
                          }));
                        }
                      }}
                      placeholder="Select an event..."
                      optionLabel="title"
                      optionValue="id"
                    />
                  </div>
                )}

                {/* Day Selection */}
                {selectedEvent && days.length > 0 && (
                  <div className="upload-field">
                    <label className="upload-field-label">Day *</label>
                    <SearchableSelect
                      options={days}
                      value={selectedDay}
                      onChange={setSelectedDay}
                      placeholder="Select a day..."
                      optionLabel="date"
                      optionValue="event_id"
                    />
                  </div>
                )}

                {/* Session Selection */}
                {selectedDay && sessions.length > 0 && (
                  <div className="upload-field">
                    <label className="upload-field-label">Session *</label>
                    <SearchableSelect
                      options={sessions}
                      value={sessions.find(s => s.ID === formData.faicon_session_id || s.id === formData.faicon_session_id)}
                      onChange={(session) => {
                        setFormData(prev => ({
                          ...prev,
                          faicon_session_id: session?.ID || session?.id || "",
                          title: session?.post_title || session?.title || session?.name || ""
                        }));
                      }}
                      placeholder="Select a session..."
                      optionLabel={typeOfTitle === "WordPress" ? "post_title" : "name"}
                      optionValue={typeOfTitle === "WordPress" ? "ID" : "id"}
                    />
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Form Fields */}
          <div className="upload-card">
            <div className="upload-card-head">
              <DocumentTextIcon />
              <h2>Transcript details</h2>
              <p>Provide a title and additional metadata</p>
            </div>
            <div className="upload-card-body">
              <div className="upload-field">
                <InputField
                  label="Title *"
                  name="title"
                  value={formData.title}
                  onChange={(value) => setFormData(prev => ({ ...prev, title: value }))}
                  placeholder="Enter transcript title"
                  required
                />
              </div>

              <div className="upload-field">
                <InputField
                  label="Description"
                  name="description"
                  value={formData.description}
                  onChange={(value) => setFormData(prev => ({ ...prev, description: value }))}
                  placeholder="Enter transcript description"
                />
              </div>

              <div className="upload-field">
                <TagOptions
                  selectedTags={formData.tags}
                  onTagsChange={(tags) => setFormData(prev => ({ ...prev, tags }))}
                  availableTags={[
                    "Diplomacy", "International Relations", "Policy", "Security",
                    "Economics", "Human Rights", "Environment", "Technology",
                    "Education", "Health", "Migration", "Conflict Resolution"
                  ]}
                />
              </div>

              <div className="upload-field upload-field-last">
                <SpeakerField
                  speakers={speakers}
                  onSpeakersChange={setSpeakers}
                  onSpeakersUpdate={(updatedSpeakers) => {
                    setFormData(prev => ({ ...prev, speakers: updatedSpeakers }));
                  }}
                />
              </div>
            </div>
          </div>

          {/* Video Upload Section */}
          <div className="upload-card">
            <div className="upload-card-head">
              <CloudArrowUpIcon />
              <h2>File upload</h2>
              <p>Audio or video file — you can continue filling the form while it uploads</p>
            </div>
            <div className="upload-card-body">
              {!file && !uploading && (
                <FileUpload
                  onFileUpload={handleFileUpload}
                  disabled={file !== null || uploading}
                />
              )}

              {file && (
                <div className={`upload-file-card ${isFileUploaded && uploadedFileId ? 'success' : ''}`}>
                  <div className="upload-file-info">
                    {isFileUploaded && uploadedFileId ? (
                      <CheckCircleIcon className="upload-file-icon success" />
                    ) : (
                      <DocumentTextIcon className="upload-file-icon" />
                    )}
                    <div className="upload-file-meta">
                      <div className="upload-file-name">{file.name}</div>
                      <div className="upload-file-sub">
                        {(file.size / 1024 / 1024).toFixed(2)} MB
                        {isFileUploaded && uploadedFileId && (
                          <>
                            <span className="upload-file-dot">·</span>
                            <span className="upload-file-success">Uploaded successfully</span>
                            <span className="upload-file-dot">·</span>
                            <span className="upload-file-id">ID {uploadedFileId}</span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      if (uploadedFileId) {
                        setShowDeleteModal(true);
                      } else {
                        setFile(null);
                        setUploadedFileId(null);
                        setIsFileUploaded(false);
                        resetUpload();
                      }
                    }}
                    className="upload-file-remove"
                    title={uploadedFileId ? "Delete uploaded file" : "Remove selected file"}
                  >
                    {isFileUploaded && uploadedFileId ? (
                      <TrashIcon />
                    ) : (
                      <XMarkIcon />
                    )}
                  </button>
                </div>
              )}

              {uploading && progress > 0 && (
                <div className="upload-progress">
                  <div className="upload-progress-head">
                    <span>{progress >= 100 ? 'Finalizing upload...' : 'Uploading...'}</span>
                    <span className="upload-progress-pct">{Math.min(progress, 100)}%</span>
                  </div>
                  <div className="upload-progress-track">
                    <div
                      className="upload-progress-bar"
                      style={{ width: `${Math.min(progress, 100)}%` }}
                    />
                  </div>
                </div>
              )}

              {uploadError && (
                <div className="upload-error">
                  <XMarkIcon className="upload-error-icon" />
                  <p>{uploadError}</p>
                </div>
              )}
            </div>
          </div>

          {/* Submit / Options bar */}
          <div className="upload-actions">
            <div className="upload-actions-options">
              <label className="upload-checkbox">
                <input
                  type="checkbox"
                  id="autoGenerateTranscript"
                  checked={formData.auto_generate_transcript}
                  onChange={(e) => handleInputChange('auto_generate_transcript', e.target.checked)}
                />
                <span>Auto‑generate transcript</span>
              </label>

              {formData.auto_generate_transcript && (
                <div className="upload-provider">
                  <label htmlFor="transcriptProvider">Provider</label>
                  <select
                    id="transcriptProvider"
                    value={selectedProvider}
                    onChange={(e) => setSelectedProvider(e.target.value)}
                  >
                    {TRANSCRIPT_PROVIDERS.map((provider) => (
                      <option key={provider.value} value={provider.value}>
                        {provider.label}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>

            <button
              type="submit"
              disabled={!canSubmit}
              className="upload-submit"
            >
              Submit transcript
            </button>
          </div>
        </form>
      )}

      {/* Instructions when no source is selected */}
      {!selectedSource && (
        <div className="upload-hint">
          <AcademicCapIcon />
          <div>
            <h3>Select a content source</h3>
            <p>
              Pick WordPress, FAICON, or Custom above to begin. The form will appear below
              once a source is selected.
            </p>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => {
          console.log('Delete modal closed');
          setShowDeleteModal(false);
        }}
        title="Delete File"
        size="sm"
      >
        <div className="upload-delete-modal">
          <div className="upload-delete-icon">
            <TrashIcon />
          </div>
          <h3>Delete uploaded file?</h3>
          <p>
            This action cannot be undone. The file will be permanently deleted from the server.
          </p>
          <div className="upload-delete-actions">
            <button
              type="button"
              onClick={() => setShowDeleteModal(false)}
              className="upload-btn upload-btn-secondary"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleDeleteFile}
              disabled={isDeleting}
              className="upload-btn upload-btn-danger"
            >
              {isDeleting ? 'Deleting...' : 'Delete file'}
            </button>
          </div>
        </div>
      </Modal>

      {/* Upload Another File */}
      {createdSessionId && (
        <div className="upload-another">
          <button
            type="button"
            onClick={resetState}
            className="upload-btn upload-btn-primary"
          >
            Upload another file
          </button>
        </div>
      )}

      {/* Notification */}
      {notification && (
        <Notification
          type={notification.type}
          title={notification.title}
          message={notification.message}
          onClose={() => setNotification(null)}
          duration={5000}
        />
      )}
    </div>
  );
}